import React, { useEffect, useRef } from "react";
import { X } from "lucide-react";
import { isTopDialog, pushDialog, removeDialog, trapTabKey } from "./dialogStack";
import { useAnimatedClose } from "../../hooks/useAnimatedClose";

const EXIT_DURATION_MS = 220;

function Drawer({ side = "left", title, onClose, children }) {
  const closeButtonRef = useRef(null);
  const lastFocusedRef = useRef(null);

  // Closing from inside the drawer slides it out first. useAnimatedClose
  // reads onClose through a ref, so a new onClose identity never re-runs the
  // effect below (which would restore focus to the opener and then re-focus
  // the close button on every parent render).
  const { closing, requestClose } = useAnimatedClose(onClose, EXIT_DURATION_MS);

  useEffect(() => {
    lastFocusedRef.current = document.activeElement;
    const token = pushDialog();
    // Deferred a tick: focusing synchronously inside this effect (which
    // itself commits inside the click handler that opened the drawer)
    // triggers a spurious "component suspended inside an act scope"
    // warning under React 19 + jsdom. A micro-delay avoids it and is
    // imperceptible to the user.
    const focusTimer = setTimeout(() => closeButtonRef.current?.focus(), 0);

    // Only the top-most dialog reacts, so Escape inside a Modal opened from
    // this drawer closes just that Modal.
    const handleKeyDown = (event) => {
      if (!isTopDialog(token)) return;

      if (event.key === "Escape") {
        requestClose();
        return;
      }

      if (event.key === "Tab") {
        trapTabKey(event, closeButtonRef.current?.closest(".drawer"));
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => {
      clearTimeout(focusTimer);
      removeDialog(token);
      document.removeEventListener("keydown", handleKeyDown);
      if (lastFocusedRef.current instanceof HTMLElement) {
        lastFocusedRef.current.focus();
      }
    };
    // requestClose is stable for the drawer's lifetime.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className={`drawer-overlay${closing ? " is-closing" : ""}`} onMouseDown={requestClose}>
      <div
        className={`drawer drawer-${side}`}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        onMouseDown={(event) => event.stopPropagation()}
      >
        <div className="drawer-header">
          <h2>{title}</h2>
          <button type="button" className="icon-btn" aria-label="Close panel" onClick={requestClose} ref={closeButtonRef}>
            <X size={18} strokeWidth={1.75} aria-hidden="true" />
          </button>
        </div>
        <div className="drawer-body">{children}</div>
      </div>
    </div>
  );
}

export default Drawer;
